import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useDarkMode } from "../../../context/ThemeContext";
import {
  fetchControllerHistoryAndWait,
  fetchHikvisionDevice,
  type ControllerHistoryRecord,
  type ControllerHistoryFilter,
  type HikvisionReader,
} from "../../../store/oasis/hikvisionAccessStore";
import ExportButtons from "../../reports/components/ExportButtons";
import type { ReportTableColumn } from "../../reports/components/ReportPointsTable";

// ─────────────────────────────────────────────────────────────────────────────
//  Controller Records — reads the access controller's STORED event log.
//
//  This is the "mirror previous tag passes" / double-check view. The real-time
//  pipeline (bridge → relay → cloud → yardAccessEvents) is untouched and keeps
//  recording live events; this screen is a read-only window onto whatever the
//  controller itself still has retained, fetched on demand.
//
//  Cloud-routed: the screen asks our API, the on-site relay polls the request,
//  reads the controller's log via the local SDK bridge, and returns it. So this
//  works from ANY machine — no need to run V5 on the site PC. Read-only:
//  nothing is written to yardAccessEvents, so there's no duplication.
// ─────────────────────────────────────────────────────────────────────────────

interface ThemeClasses {
  bg: string;
  border: string;
  text: string;
  textSecondary: string;
  cardBg: string;
  cardBorder: string;
  buttonHover: string;
  buttonText: string;
}

type ControllerRecord = ControllerHistoryRecord;

// Tope real por consulta: el servidor valida `max` <= 2000
// (hikvisionAccessSchema.postHikvisionHistoryRequest) y guarda como mucho 2000
// eventos por request (MAX_STORED en /history/result).
const MAX_RECORDS = 2000;

// ─── Event classification (mirrors the cloud's hikvisionService taxonomy) ─────

const MAJOR_CARD = 1; // card / biometric auth
const MAJOR_ALARM = 3; // tamper, door forced…
const MAJOR_REMOTE = 4; // remote open/close
const MAJOR_DEVICE = 5; // device/door-state + HikGateway-translated access

const GRANTED: Record<number, Set<number>> = {
  [MAJOR_CARD]: new Set([1, 4, 5, 21, 75]),
  [MAJOR_REMOTE]: new Set([1]),
  [MAJOR_DEVICE]: new Set([
    1, 2, 15, 38, 40, 43, 46, 54, 57, 60, 63, 66, 75, 156, 159,
  ]),
};
const DENIED: Record<number, Set<number>> = {
  [MAJOR_CARD]: new Set([2, 3]),
  [MAJOR_DEVICE]: new Set([
    3, 6, 7, 8, 9, 10, 11, 12, 13, 39, 41, 44, 47, 55, 58, 61, 64, 67, 76,
    80, 113, 157, 160,
  ]),
};
// Door open/close with NO credential (exit-button 23, manual handle,
// remote/operator open, door-contact state). Confirmed on this DS-K2812: these
// minors never carry a cardNo, unlike the credential minors (5/1 grant, 5/9
// deny) — so they are door operations, not card decisions, and get their own
// "manual" bucket instead of inflating granted/denied. Keep in sync with the
// bridge's DoorOpMinors (apps/hikvision-sdk-bridge/EventHistory.cs).
const DOOR_OP: Record<number, Set<number>> = {
  [MAJOR_DEVICE]: new Set([21, 22, 23, 31, 32]),
};

type RecordResult = "granted" | "denied" | "manual" | "alarm" | "other";

function classify(r: ControllerRecord): RecordResult {
  if (DOOR_OP[r.major]?.has(r.minor)) return "manual";
  if (GRANTED[r.major]?.has(r.minor)) return "granted";
  if (DENIED[r.major]?.has(r.minor)) return "denied";
  if (r.major === MAJOR_ALARM) return "alarm";
  return "other";
}

function resultLabel(res: RecordResult, t: TFunction): string {
  return t(`accessRecords.results.${res}`);
}

function resultClasses(res: RecordResult): string {
  if (res === "granted")
    return "bg-green-500/15 text-green-400 border border-green-500/30";
  if (res === "denied")
    return "bg-red-500/15 text-red-400 border border-red-500/30";
  if (res === "manual")
    return "bg-blue-500/15 text-blue-400 border border-blue-500/30";
  if (res === "alarm")
    return "bg-orange-500/15 text-orange-400 border border-orange-500/30";
  return "bg-yellow-500/15 text-yellow-400 border border-yellow-500/30";
}

// Direction comes from the device's configured readers[] map (reader_no →
// direction) — the same signal the cloud uses in `directionFromReader`
// (hikvisionService.js), so the label and the stored event_type always agree.
//
// It is NOT derived from the reader number's parity. Hikvision reserves two
// reader slots per door (door 1 → 1,2; door 2 → 3,4; …), so on a two-door
// controller the entry reader of door 2 is number 3 — odd, yet an exit lane.
// Acuapue is exactly that case: reader 1 = pluma de entrada, reader 3 = pluma
// de salida. Guessing by parity mislabelled every exit swipe as "(entrada)".
//
// With no readers[] configured we show no direction rather than invent one.
function readerDirection(
  readerNo: number,
  readers: HikvisionReader[] | null | undefined,
): "entry" | "exit" | "unknown" {
  if (!readerNo || !Array.isArray(readers)) return "unknown";
  const match = readers.find((r) => Number(r.reader_no) === Number(readerNo));
  if (match?.direction === "entry" || match?.direction === "exit") {
    return match.direction;
  }
  return "unknown";
}

function readerLabel(
  readerNo: number,
  readers: HikvisionReader[] | null | undefined,
  t: TFunction,
): string {
  if (!readerNo) return "—";
  const dir = readerDirection(readerNo, readers);
  const suffix =
    dir === "entry"
      ? ` (${t("accessRecords.directionEntry")})`
      : dir === "exit"
        ? ` (${t("accessRecords.directionExit")})`
        : "";
  // Prefer the operator-facing name from the device config ("Lectora Salida")
  // over the bare index, so the column reads the same as the console.
  const named = readers?.find(
    (r) => Number(r.reader_no) === Number(readerNo),
  )?.name;
  return named ? `${named} (#${readerNo})` : `${t("accessRecords.readerNumbered", { n: readerNo })}${suffix}`;
}

function formatTime(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale, { dateStyle: "short", timeStyle: "medium" });
}

function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Day bucket (yyyy-mm-dd) used as the unique value for the date-column filter —
// ISO so it sorts chronologically and groups every event of a day together.
function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return toDateInput(d);
}

// ─── Per-column filtering ─────────────────────────────────────────────────────
// Each table header gets a dropdown listing the unique values present in the
// loaded records for that column. The user ticks the values they want to keep;
// an empty selection means "no filter" for that column. Columns AND together.

type ColKey = "time" | "card" | "reader" | "door" | "result" | "type";

interface ColumnDef {
  key: ColKey;
  label: string;
  getValue: (r: ControllerRecord) => string;
}

// Built per-device: the "Lectora" bucket depends on the device's readers[] map,
// so the filter dropdown lists the same labels the table renders.
function buildColumns(
  readers: HikvisionReader[] | null | undefined,
  t: TFunction,
): ColumnDef[] {
  return [
    { key: "time", label: t("accessRecords.columns.time"), getValue: (r) => dayKey(r.time) },
    { key: "card", label: t("accessRecords.columns.card"), getValue: (r) => r.cardNo || "—" },
    { key: "reader", label: t("accessRecords.columns.reader"), getValue: (r) => readerLabel(r.cardReaderNo, readers, t) },
    { key: "door", label: t("accessRecords.columns.door"), getValue: (r) => `#${r.doorNo}` },
    { key: "result", label: t("accessRecords.columns.result"), getValue: (r) => resultLabel(classify(r), t) },
    { key: "type", label: t("accessRecords.columns.type"), getValue: (r) => `${r.major}/${r.minor}` },
  ];
}

// ─── Export rows ──────────────────────────────────────────────────────────────
// Flattened, display-ready shape for the Excel/PDF export (reuses the reports'
// exportHistoric utility). Unlike the header-filter COLUMNS (which bucket the
// time into a day key), here the time column carries the full timestamp.

interface AccessExportRow {
  id: string;
  time: string;
  card: string;
  reader: string;
  door: string;
  result: string;
  type: string;
}

const exportColumns = (t: TFunction): ReportTableColumn<AccessExportRow>[] =>
  (["time", "card", "reader", "door", "result", "type"] as const).map((key) => ({
    key,
    header: t(`accessRecords.columns.${key}`),
  }));

type ColumnFilters = Record<ColKey, Set<string>>;

function emptyFilters(): ColumnFilters {
  return {
    time: new Set(),
    card: new Set(),
    reader: new Set(),
    door: new Set(),
    result: new Set(),
    type: new Set(),
  };
}

// Header dropdown. Rendered with position:fixed (anchored to its button) so the
// table's overflow-hidden / overflow-x-auto wrappers can't clip it.
function ColumnFilter({
  values,
  selected,
  onChange,
  isDarkMode,
}: {
  values: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  isDarkMode: boolean;
}) {
  const { t } = useTranslation("truckYard");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const active = selected.size > 0;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? values.filter((v) => v.toLowerCase().includes(q)) : values;
  }, [values, query]);

  const openMenu = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      const width = 224;
      const left = Math.min(rect.left, window.innerWidth - width - 8);
      setPos({ top: rect.bottom + 4, left: Math.max(8, left) });
    }
    setQuery("");
    setOpen(true);
  };

  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next);
  };

  const panelBg = isDarkMode
    ? "bg-dark-card border-dark-border"
    : "bg-white border-gray-200";
  const itemHover = isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-100";
  const textCls = isDarkMode ? "text-gray-200" : "text-gray-800";
  const subText = isDarkMode ? "text-gray-400" : "text-gray-500";
  const inputBg = isDarkMode
    ? "bg-[#1d1d1d] text-white border-dark-border"
    : "bg-gray-100 text-gray-900 border-gray-200";

  return (
    <span className="relative inline-flex">
      <button
        ref={btnRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openMenu())}
        title={t("accessRecords.columnFilter.title")}
        className={`relative inline-flex items-center justify-center w-5 h-5 rounded transition-colors ${
          active
            ? "text-primary-500"
            : isDarkMode
              ? "text-gray-500 hover:text-gray-300"
              : "text-gray-400 hover:text-gray-600"
        }`}
      >
        <i
          className={`${active ? "ri-filter-fill" : "ri-filter-3-line"} text-sm`}
        ></i>
        {active && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-1 rounded-full bg-primary-500 text-gray-900 text-[9px] font-bold leading-[14px] text-center">
            {selected.size}
          </span>
        )}
      </button>

      {open && pos && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div
            style={{ position: "fixed", top: pos.top, left: pos.left, width: 224 }}
            className={`z-50 rounded-lg border shadow-xl p-2 normal-case tracking-normal font-normal ${panelBg}`}
          >
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("accessRecords.columnFilter.search")}
              className={`w-full mb-2 px-2 py-1.5 rounded-md text-xs outline-none border ${inputBg}`}
            />
            <div className="flex items-center justify-between mb-1 px-1">
              <button
                type="button"
                onClick={() => onChange(new Set(values))}
                className="text-[11px] text-primary-500 hover:underline"
              >
                {t("accessRecords.columnFilter.all")}
              </button>
              <button
                type="button"
                onClick={() => onChange(new Set())}
                className={`text-[11px] ${subText} hover:underline`}
              >
                {t("accessRecords.columnFilter.clear")}
              </button>
            </div>
            <div className="max-h-56 overflow-auto">
              {filtered.length === 0 ? (
                <p className={`text-[11px] ${subText} px-2 py-3 text-center`}>
                  {t("accessRecords.columnFilter.noMatches")}
                </p>
              ) : (
                filtered.map((v) => (
                  <label
                    key={v}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-xs ${textCls} ${itemHover}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(v)}
                      onChange={() => toggle(v)}
                      className="accent-primary-500 shrink-0"
                    />
                    <span className="truncate" title={v}>
                      {v}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </span>
  );
}

// ─── Screen ──────────────────────────────────────────────────────────────────

export default function AccessRecordsSection() {
  const { t, i18n } = useTranslation("truckYard");
  const locale = i18n.language?.startsWith("en") ? "en-US" : "es-MX";
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const deviceId = params.get("deviceId") || "";
  const deviceName = params.get("name") || t("accessRecords.defaultDeviceName");
  const serial = params.get("serial") || "";
  const ip = params.get("ip") || "";
  const yardName = params.get("yard") || "";

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
  };
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const inputCls = `px-3 py-2 ${
    isDarkMode ? "bg-dark-card text-white" : "bg-gray-100 text-gray-900"
  } border ${themeClasses.cardBorder} rounded-lg outline-none text-sm focus:ring-2 focus:ring-primary-500/30`;

  const now = useMemo(() => new Date(), []);
  const monthAgo = useMemo(
    () => new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
    [now],
  );

  const [allHistory, setAllHistory] = useState(false);
  const [dateFrom, setDateFrom] = useState(toDateInput(monthAgo));
  const [dateTo, setDateTo] = useState(toDateInput(now));
  // Which event type to pull from the controller (server-side, so the per-fetch
  // cap applies to the wanted type only). "access" = real access events
  // (granted/denied card reads, exit-button, remote open); the others narrow to
  // a single result bucket. See ControllerHistoryFilter.
  const [eventType, setEventType] = useState<ControllerHistoryFilter>("access");
  // Capped at 2000 — the relay returns the array inside one Firestore doc
  // (~1 MiB limit). Narrow the date range to see beyond that.
  const [maxRecords, setMaxRecords] = useState(MAX_RECORDS);

  const [records, setRecords] = useState<ControllerRecord[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<{
    count?: number;
    truncated?: boolean;
  } | null>(null);

  // The device's readers[] map — the authoritative reader→direction source for
  // the "Lectora" column. Fetched once per device; a failure just leaves the
  // labels without a direction suffix, never blocks the records view.
  const [readers, setReaders] = useState<HikvisionReader[] | null>(null);

  useEffect(() => {
    if (!deviceId) {
      setReaders(null);
      return;
    }
    let cancelled = false;
    fetchHikvisionDevice(deviceId)
      .then((d) => {
        if (!cancelled) setReaders(Array.isArray(d.readers) ? d.readers : null);
      })
      .catch(() => {
        if (!cancelled) setReaders(null);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  const columns = useMemo(() => buildColumns(readers, t), [readers, t]);

  // Per-column header filters.
  const [filters, setFilters] = useState<ColumnFilters>(emptyFilters);

  const setColumnFilter = useCallback((key: ColKey, next: Set<string>) => {
    setFilters((prev) => ({ ...prev, [key]: next }));
  }, []);

  const anyFilterActive = columns.some((c) => filters[c.key].size > 0);

  // Unique values present in the loaded records, per column.
  const uniqueValues = useMemo<Record<ColKey, string[]>>(() => {
    const out = emptyFilters() as unknown as Record<ColKey, Set<string>>;
    for (const r of records ?? []) {
      for (const col of columns) out[col.key].add(col.getValue(r));
    }
    const result = {} as Record<ColKey, string[]>;
    for (const col of columns) {
      const list = Array.from(out[col.key]);
      // time uses ISO day keys → newest first; everything else natural/numeric.
      list.sort((a, b) =>
        col.key === "time"
          ? b.localeCompare(a)
          : a.localeCompare(b, locale, { numeric: true }),
      );
      result[col.key] = list;
    }
    return result;
    // `columns` changes when the device's readers[] map arrives — the "Lectora"
    // buckets must be recomputed with the corrected labels.
  }, [records, columns, locale]);

  // Rows after applying every active column filter (columns AND together).
  const visibleRecords = useMemo(() => {
    if (!records) return records;
    const activeCols = columns.filter((c) => filters[c.key].size > 0);
    if (activeCols.length === 0) return records;
    return records.filter((r) =>
      activeCols.every((c) => filters[c.key].has(c.getValue(r))),
    );
  }, [records, filters, columns]);

  // Export rows follow the active column filters (the whole filtered set, not
  // just the visible page), formatted the same way the table cells render.
  const exportRows = useMemo<AccessExportRow[]>(
    () =>
      (visibleRecords ?? []).map((r, i) => ({
        id: `${r.time}-${i}`,
        time: formatTime(r.time, locale),
        card: r.cardNo || "—",
        reader: readerLabel(r.cardReaderNo, readers, t),
        door: `#${r.doorNo}`,
        result: resultLabel(classify(r), t),
        type: `${r.major}/${r.minor}`,
      })),
    [visibleRecords, readers, t, locale],
  );

  const exportPeriod = allHistory
    ? t("accessRecords.exportPeriodAll")
    : t("accessRecords.exportPeriodRange", {
        from: dateFrom || t("accessRecords.exportFrom"),
        to: dateTo || t("accessRecords.exportTo"),
      });
  const exportFileBase = `registros_controlador_${deviceName.replace(/\s+/g, "_")}`;

  // Pagination over the filtered rows.
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const totalRows = visibleRecords?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, pageCount);
  const pagedRecords = useMemo(
    () => (visibleRecords ?? []).slice((safePage - 1) * pageSize, safePage * pageSize),
    [visibleRecords, safePage, pageSize],
  );

  // Snap back to page 1 whenever the active filters change the result set.
  const filtersSig = columns.map((c) => `${c.key}:${filters[c.key].size}`).join("|");
  const prevSig = useRef(filtersSig);
  if (prevSig.current !== filtersSig) {
    prevSig.current = filtersSig;
    if (page !== 1) setPage(1);
  }

  const fetchRecords = useCallback(async () => {
    if (!deviceId) {
      setError(t("accessRecords.errors.missingDevice"));
      return;
    }

    setLoading(true);
    setError(null);
    setRecords(null);
    setMeta(null);

    try {
      const result = await fetchControllerHistoryAndWait({
        device_id: deviceId,
        from: allHistory || !dateFrom ? null : dateFrom,
        // include the whole "to" day
        to: allHistory || !dateTo ? null : `${dateTo}T23:59:59`,
        max: Math.min(maxRecords, MAX_RECORDS),
        filter: eventType,
      });

      if (result.status === "failed") {
        setError(
          result.error || t("accessRecords.errors.failed"),
        );
        return;
      }
      if (result.status !== "completed") {
        setError(t("accessRecords.errors.timeout"));
        return;
      }

      const evs = [...(result.events || [])];
      // Newest first.
      evs.sort((a, b) => (a.time < b.time ? 1 : a.time > b.time ? -1 : 0));
      setRecords(evs);
      setFilters(emptyFilters());
      setPage(1);
      setMeta({
        count: result.count ?? evs.length,
        truncated: result.truncated,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("accessRecords.errors.request"),
      );
    } finally {
      setLoading(false);
    }
  }, [deviceId, allHistory, dateFrom, dateTo, maxRecords, eventType, t]);

  return (
    // Whole page is one vertical scroller, so the title + filters scroll away
    // and the table's column headers then stick to the top of the viewport.
    <div className={`h-full overflow-y-auto ${themeClasses.bg}`}>
      {/* Header */}
      <div className={`${themeClasses.bg} border-b ${themeClasses.border} p-4 sm:p-6`}>
        <button
          onClick={() => navigate(-1)}
          className={`mb-3 inline-flex items-center gap-1.5 text-sm ${themeClasses.buttonText} ${themeClasses.buttonHover} px-2 py-1 rounded-lg transition-colors`}
        >
          <i className="ri-arrow-left-line"></i>
          {t("accessRecords.back")}
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <p className={`text-xs ${themeClasses.textSecondary} mb-0.5`}>
              {t("accessRecords.breadcrumb")}{yardName ? ` · ${yardName}` : ""}
            </p>
            <h1 className={`text-xl font-bold ${themeClasses.text} flex flex-wrap items-center gap-2`}>
              {t("accessRecords.title")}
              {meta?.count != null && (
                <span className={`text-sm font-normal ${themeClasses.textSecondary}`}>
                  ({meta.count})
                </span>
              )}
            </h1>
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {deviceName}
              {serial ? ` · ${t("accessRecords.serialPrefix")} ${serial}` : ""}
              {ip ? ` · ${ip}` : ""}
            </p>
            <p className={`text-[11px] ${themeClasses.textSecondary} mt-1`}>
              <i className="ri-information-line mr-1"></i>
              {t("accessRecords.readOnlyNote")}
            </p>
          </div>

          {records !== null && records.length > 0 && (
            <ExportButtons
              rows={exportRows}
              columns={exportColumns(t)}
              meta={{
                title: t("accessRecords.title"),
                period: exportPeriod,
                fileBase: exportFileBase,
              }}
              className="self-start sm:self-auto"
            />
          )}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div>
            <label className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium block mb-1`}>
              {t("accessRecords.from")}
            </label>
            <input
              type="date"
              value={dateFrom}
              disabled={allHistory}
              onChange={(e) => setDateFrom(e.target.value)}
              className={`${inputCls} w-full disabled:opacity-40`}
            />
          </div>
          <div>
            <label className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium block mb-1`}>
              {t("accessRecords.to")}
            </label>
            <input
              type="date"
              value={dateTo}
              disabled={allHistory}
              onChange={(e) => setDateTo(e.target.value)}
              className={`${inputCls} w-full disabled:opacity-40`}
            />
          </div>
          <div>
            <label className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium block mb-1`}>
              {t("accessRecords.eventType")}
            </label>
            <select
              value={eventType}
              onChange={(e) =>
                setEventType(e.target.value as ControllerHistoryFilter)
              }
              className={`${inputCls} w-full`}
              title={t("accessRecords.eventTypeTitle")}
            >
              {(["access", "granted", "denied", "manual", "alarm", "other", "all"] as const).map((v) => (
                <option key={v} value={v}>
                  {t(`accessRecords.filters.${v}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium block mb-1`}>
              {t("accessRecords.maxRecords")}
            </label>
            <input
              type="number"
              min={1}
              max={MAX_RECORDS}
              value={maxRecords}
              onChange={(e) =>
                setMaxRecords(Math.min(MAX_RECORDS, Math.max(1, Number(e.target.value) || MAX_RECORDS)))
              }
              className={`${inputCls} w-full`}
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchRecords}
              disabled={loading}
              className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm transition-colors disabled:opacity-50"
            >
              {loading ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-download-cloud-line"></i>
              )}
              {loading ? t("accessRecords.loadingButton") : t("accessRecords.load")}
            </button>
          </div>
          <label className={`flex items-center gap-2 text-xs ${themeClasses.textSecondary} sm:col-span-2 lg:col-span-5`}>
            <input
              type="checkbox"
              checked={allHistory}
              onChange={(e) => setAllHistory(e.target.checked)}
              className="accent-primary-500"
            />
            {t("accessRecords.allHistory")}
          </label>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 sm:p-6">
        {/* Truncation note */}
        {meta?.truncated && (
          <div className="mb-4 rounded-xl p-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {t("accessRecords.truncated", { max: maxRecords.toLocaleString(locale) })}
          </div>
        )}

        {/* Error / fallback ("backfall") state */}
        {error && (
          <div className="rounded-2xl p-6 bg-red-500/10 border border-red-500/30">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-plug-line text-xl text-red-400"></i>
              <h3 className="text-base font-semibold text-red-400">
                {t("accessRecords.errorTitle")}
              </h3>
            </div>
            <p className={`text-sm ${themeClasses.text} mb-3`}>{error}</p>
            <ul className={`text-xs ${themeClasses.textSecondary} space-y-1 list-disc pl-5`}>
              {(["relay", "bridge", "ivms", "range"] as const).map((k) => (
                <li key={k}>{t(`accessRecords.hints.${k}`)}</li>
              ))}
            </ul>
            <button
              onClick={fetchRecords}
              className="mt-4 px-4 py-2 rounded-lg text-sm font-medium border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <i className="ri-refresh-line mr-1"></i>
              {t("accessRecords.retry")}
            </button>
          </div>
        )}

        {/* Idle */}
        {!error && !loading && records === null && (
          <div className={`rounded-2xl p-10 text-center ${surface}`}>
            <i className={`ri-history-line text-4xl ${themeClasses.textSecondary} mb-3 block`}></i>
            <p className={`text-sm ${themeClasses.text} font-medium`}>
              {t("accessRecords.idleTitle")}
            </p>
            <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
              {t("accessRecords.idleText")}
            </p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className={`rounded-2xl p-10 text-center ${surface}`}>
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-3 block"></i>
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {t("accessRecords.loadingText")}
            </p>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && records !== null && records.length === 0 && (
          <div className={`rounded-2xl p-10 text-center ${surface}`}>
            <i className={`ri-inbox-line text-4xl ${themeClasses.textSecondary} mb-3 block`}></i>
            <p className={`text-sm ${themeClasses.text} font-medium`}>
              {t("accessRecords.emptyTitle")}
            </p>
            <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
              {t("accessRecords.emptyText")}
            </p>
          </div>
        )}

        {/* Records table */}
        {!loading && !error && records !== null && records.length > 0 && (
          <div className={`rounded-2xl border ${themeClasses.cardBorder}`}>
            {/* Filter summary bar */}
            {anyFilterActive && (
              <div className={`flex items-center justify-between gap-3 px-4 py-2 border-b rounded-t-2xl ${themeClasses.cardBorder} ${surface}`}>
                <span className={`text-xs ${themeClasses.textSecondary}`}>
                  <i className="ri-filter-3-line mr-1"></i>
                  {t("accessRecords.filteredCount", {
                    shown: totalRows.toLocaleString(locale),
                    total: records.length.toLocaleString(locale),
                  })}
                </span>
                <button
                  type="button"
                  onClick={() => setFilters(emptyFilters())}
                  className={`text-xs ${themeClasses.textSecondary} hover:text-primary-500 transition-colors inline-flex items-center gap-1`}
                >
                  <i className="ri-close-circle-line"></i>
                  {t("accessRecords.clearFilters")}
                </button>
              </div>
            )}

            {/* No inner vertical scroll — the page scrolls; the sticky thead
                below sticks to the viewport. Plain div keeps it from becoming a
                scroll container (which would trap the sticky header). */}
            <div className="w-full">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left">
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`sticky top-0 z-20 px-4 py-3 text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium border-b ${themeClasses.cardBorder} ${surface}`}
                      >
                        <div className="flex items-center gap-1 whitespace-nowrap">
                          <span>{col.label}</span>
                          <ColumnFilter
                            values={uniqueValues[col.key]}
                            selected={filters[col.key]}
                            onChange={(next) => setColumnFilter(col.key, next)}
                            isDarkMode={isDarkMode}
                          />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedRecords.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className={`px-4 py-10 text-center text-sm ${themeClasses.textSecondary} ${themeClasses.cardBg}`}
                      >
                        <i className="ri-filter-off-line mr-1"></i>
                        {t("accessRecords.noMatches")}
                      </td>
                    </tr>
                  ) : (
                    pagedRecords.map((r, i) => {
                      const res = classify(r);
                      return (
                        <tr
                          key={`${r.time}-${i}`}
                          className={`border-t ${themeClasses.cardBorder} ${themeClasses.cardBg}`}
                        >
                          <td className={`px-4 py-3 ${themeClasses.text} whitespace-nowrap`}>
                            {formatTime(r.time, locale)}
                          </td>
                          <td className={`px-4 py-3 font-mono ${themeClasses.text}`}>
                            {r.cardNo || (
                              <span className={themeClasses.textSecondary}>—</span>
                            )}
                          </td>
                          <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>
                            {readerLabel(r.cardReaderNo, readers, t)}
                          </td>
                          <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>
                            #{r.doorNo}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${resultClasses(res)}`}>
                              {resultLabel(res, t)}
                            </span>
                          </td>
                          <td className={`px-4 py-3 font-mono text-xs ${themeClasses.textSecondary}`}>
                            {r.major}/{r.minor}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-t rounded-b-2xl ${themeClasses.cardBorder} ${surface}`}>
              <div className={`flex items-center gap-2 text-xs ${themeClasses.textSecondary}`}>
                <span>{t("accessRecords.rowsPerPage")}</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className={`px-2 py-1 rounded-md border ${themeClasses.cardBorder} ${
                    isDarkMode ? "bg-dark-card text-white" : "bg-white text-gray-900"
                  } outline-none`}
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span className="ml-1">
                  {t("accessRecords.rangeOf", {
                    range:
                      totalRows === 0
                        ? "0"
                        : `${((safePage - 1) * pageSize + 1).toLocaleString(locale)}–${Math.min(
                            safePage * pageSize,
                            totalRows,
                          ).toLocaleString(locale)}`,
                    total: totalRows.toLocaleString(locale),
                  })}
                </span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage(1)}
                  disabled={safePage <= 1}
                  className={`w-8 h-8 rounded-md inline-flex items-center justify-center text-sm ${themeClasses.textSecondary} ${themeClasses.buttonHover} disabled:opacity-30 disabled:pointer-events-none`}
                  title={t("accessRecords.first")}
                >
                  <i className="ri-skip-back-mini-line"></i>
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className={`w-8 h-8 rounded-md inline-flex items-center justify-center text-sm ${themeClasses.textSecondary} ${themeClasses.buttonHover} disabled:opacity-30 disabled:pointer-events-none`}
                  title={t("accessRecords.previous")}
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <span className={`px-2 text-xs ${themeClasses.text} whitespace-nowrap`}>
                  {t("accessRecords.pageOf", { page: safePage, pages: pageCount })}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={safePage >= pageCount}
                  className={`w-8 h-8 rounded-md inline-flex items-center justify-center text-sm ${themeClasses.textSecondary} ${themeClasses.buttonHover} disabled:opacity-30 disabled:pointer-events-none`}
                  title={t("accessRecords.next")}
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
                <button
                  type="button"
                  onClick={() => setPage(pageCount)}
                  disabled={safePage >= pageCount}
                  className={`w-8 h-8 rounded-md inline-flex items-center justify-center text-sm ${themeClasses.textSecondary} ${themeClasses.buttonHover} disabled:opacity-30 disabled:pointer-events-none`}
                  title={t("accessRecords.last")}
                >
                  <i className="ri-skip-forward-mini-line"></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
